import type { FundFlow, GroupAnalysis, HolderRow, LinkedTransfer, TerminalTrade, WalletProfile } from '@pleco/shared';
import { req } from './api.js';

export type Timeframe = '1s' | '15s' | '1m' | '5m' | '15m' | '1h' | '4h' | '1d';
export const TIMEFRAMES: Timeframe[] = ['1s', '15s', '1m', '5m', '15m', '1h', '4h', '1d'];

export interface TradeSyncStatus {
  mint: string;
  trades: number;
  firstTs?: number;
  newestTs?: number;
  oldestIndexedTs?: number;
  launchDone: boolean;
  backfillDone: boolean;
  complete: boolean;
  pools: string[];
}

export interface Candle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface ChartMarker {
  t: number;
  side: 'buy' | 'sell';
  key: string;
  sol: number;
  tokens: number;
  count: number;
  wallets: number;
}

const enc = encodeURIComponent;

export const terminalApi = {
  trades: (
    mint: string,
    q: { limit?: number; before?: number; since?: number; wallet?: string[]; q?: string; side?: 'buy' | 'sell'; minSol?: number; sync?: boolean } = {},
  ) => {
    const p = new URLSearchParams();
    if (q.limit) p.set('limit', String(q.limit));
    if (q.before) p.set('before', String(q.before));
    if (q.since) p.set('since', String(Math.floor(q.since)));
    if (q.q) p.set('q', q.q);
    if (q.wallet?.length) p.set('wallet', q.wallet.join(','));
    if (q.side) p.set('side', q.side);
    if (q.minSol) p.set('minSol', String(q.minSol));
    if (q.sync === false) p.set('sync', '0');
    return req<{ status: TradeSyncStatus; trades: TerminalTrade[] }>(`/v1/sol/${enc(mint)}/trades?${p.toString()}`);
  },
  candles: (mint: string, tf: Timeframe) =>
    req<{ tf: Timeframe; candles: Candle[]; source: string; solUsd?: number }>(`/v1/sol/${enc(mint)}/candles?tf=${tf}`),
  markers: (mint: string, tf: Timeframe, wallet?: string) =>
    req<{ tf: Timeframe; markers: ChartMarker[] }>(`/v1/sol/${enc(mint)}/markers?tf=${tf}${wallet ? `&wallet=${enc(wallet)}` : ''}`),
  groups: (mint: string) => req<GroupAnalysis>(`/v1/sol/${enc(mint)}/groups`),
  flows: (mint: string, since = 0) =>
    req<{ transfers: LinkedTransfer[]; scannedAt: number; watching: boolean }>(`/v1/sol/${enc(mint)}/flows${since ? `?since=${Math.floor(since)}` : ''}`),
  holders: (mint: string, limit = 200) =>
    req<{ holders: HolderRow[]; complete: boolean; priceSol?: number; solUsd?: number }>(`/v1/sol/${enc(mint)}/holders?limit=${limit}`),
  wallet: (mint: string, address: string) => req<WalletProfile>(`/v1/sol/${enc(mint)}/wallet/${enc(address)}`),
  flow: (mint: string, address: string) => req<FundFlow>(`/v1/sol/${enc(mint)}/flow/${enc(address)}`),
};
